import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDownIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const {
    t
  } = useLanguage();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const photoY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  return <section id="home" ref={ref} className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-haa-navy" aria-label="Hero section" data-id="element-2432">
      {/* Parallax photo background */}
      <motion.div style={{
      y: photoY
    }} className="absolute inset-0 w-full h-[115%] -top-[7.5%]" data-id="element-2433">
        <img src="https://cdn.magicpatterns.com/uploads/4SeJpe14xvKU2wUKgcfuEH/Landing_pic.jpg" alt="Person holding Honduran and American flags in front of the US Capitol" className="w-full h-full object-cover object-center" data-id="element-2434" />
      </motion.div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-haa-navy/80 via-haa-navy/40 to-haa-navy/20" data-id="element-2435" />
      <div className="absolute inset-0 bg-haa-blue/10" data-id="element-2436" />

      {/* Content */}
      <motion.div style={{
      y,
      opacity
    }} className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto pt-24 lg:pt-28" data-id="element-2437">
        {/* Pre-title badge */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-4 py-2 mb-6 backdrop-blur-sm" data-id="element-2438">
          <StarIcon className="w-4 h-4 text-haa-skyblue fill-haa-skyblue" data-id="element-2439" />
          <span className="font-nunito text-white text-sm font-semibold tracking-widest uppercase" data-id="element-2440">
            {'Est. 2025'}
          </span>
          <StarIcon className="w-4 h-4 text-haa-skyblue fill-haa-skyblue" data-id="element-2441" />
        </motion.div>

        {/* Main Title */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.85
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.7,
        delay: 0.4
      }} className="flex justify-center mb-8 mt-4" data-id="element-2442">
          <div className="bg-white/50 rounded-2xl px-8 py-5 shadow-2xl backdrop-blur-sm" data-id="element-2443">
            <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association Logo" className="w-64 sm:w-80 md:w-96 object-contain" data-id="element-2444" />
          </div>
        </motion.div>

        {/* Tagline */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.6
      }} className="mb-10" data-id="element-2445">
          <p className="font-nunito italic text-white/90 text-lg sm:text-xl md:text-2xl font-600 mb-1 drop-shadow-lg" data-id="element-2446">
            {t('hero.connectTagline')}
          </p>
          <p className="font-nunito text-white/70 text-base sm:text-lg drop-shadow-md" data-id="element-2447">
            {t('hero.diaspora')}
          </p>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2" data-id="element-2448">
        <span className="font-nunito text-white/50 text-xs uppercase tracking-widest" data-id="element-2449">
          {t('hero.scroll')}
        </span>
        <div className="animate-bounce-slow" data-id="element-2450">
          <ChevronDownIcon className="w-6 h-6 text-haa-skyblue" data-id="element-2451" />
        </div>
      </motion.div>
    </section>;
}